import { useReducer } from 'react'

/**
 * Add a dummy-dependency that can be updated on-demand
 *
 * @example
 * Effect is run whenever count updates, or `refresh` is called
 * ```ts
 * const [count, setCount] = useState(0)
 * const { refreshable, refresh, key } = useRefreshable()
 * // Effect is re-run whenever refresh is called
 * useEffect(() => {
 * 	refreshable()
 * 	console.log(count)
 * }, [count, refreshable])
 * // A new element is instantiated whenever refresh is called
 * return <div key={key} />
 * ```
 */
export const useRefreshable = () => {
	const [{ refreshable, key }, refresh] = useReducer(({ key }) => ({ refreshable: () => {}, key: key + 1 }), {
		refreshable: () => {},
		key: 0,
	})

	return {
		/** Call inside a memoized hook to allow forced updates */
		refreshable,
		/** Key for forcing component rerender */
		key,
		/** Force refresh `refreshable` and `key` identity. This function is stable itself. */
		refresh: refresh as () => void,
	}
}
